import { FC, useState, useContext } from 'react';
import { Box, IconButton, Tooltip, useTheme, useMediaQuery } from '@mui/material';
import { Outlet } from 'react-router-dom';
import StoryCraftHeader from './Header';
import { HeaderUsageContext } from '../../contexts/HeaderUsageContext';
import { ThemeContext } from '../../theme/ThemeProvider';
import { useLocale } from '../../hooks/useLocale';

const HEADER_HEIGHT = 64;

const StoryCraftLayout: FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';
  const setThemeName = useContext(ThemeContext);
  const { s } = useLocale();

  const [useSearchBar, setUseSearchBar] = useState<boolean>(true);
  const [useAvatar, setUseAvatar] = useState<boolean>(true);

  const toggleTheme = () => {
    setThemeName(isDark ? 'PureLightTheme' : 'ZPSDarkTheme');
  };

  return (
    <HeaderUsageContext.Provider
      value={{
        useUpper: true,
        useLower: false,
        useSearchBar,
        setUseSearchBar,
        useAvatar,
        setUseAvatar,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
        }}
      >
        <StoryCraftHeader />

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            pt: `${HEADER_HEIGHT}px`,
            pb: isMobile ? '68px' : 0,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Outlet />
          </Box>
        </Box>

        {isMobile && <MobileBottomNav />}

        {/* Floating Discord Button */}
        <Tooltip title="Join Discord" placement="left">
          <IconButton
            onClick={() => window.open('https://discord.com/invite/qZ9Mc9SbBK', '_blank')}
            sx={{
              position: 'fixed',
              bottom: isMobile ? 136 : 80,
              right: 24,
              width: 48,
              height: 48,
              zIndex: 1300,
              backgroundColor: '#5865F2',
              boxShadow: '0 4px 16px rgba(88,101,242,0.45)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': { backgroundColor: '#4752C4', transform: 'scale(1.1)' },
              '&:active': { transform: 'scale(0.95)' },
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 24 24"
              sx={{ width: 22, height: 22, fill: '#fff' }}
            >
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </Box>
          </IconButton>
        </Tooltip>

        {/* Floating Theme Toggle */}
        <Tooltip title={isDark ? s.theme_toggle_light : s.theme_toggle_dark} placement="left">
          <IconButton
            onClick={toggleTheme}
            sx={{
              position: 'fixed',
              bottom: isMobile ? 80 : 24,
              right: 24,
              width: 48,
              height: 48,
              zIndex: 1300,
              backgroundColor: isDark ? '#252836' : '#FFFFFF',
              border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
              boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.4)' : '0 4px 16px rgba(0,0,0,0.12)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                backgroundColor: isDark ? '#2E3148' : '#F5F5F5',
                transform: 'scale(1.1)',
              },
              '&:active': { transform: 'scale(0.95)' },
            }}
          >
            {isDark ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="5" fill="#5B4DFF" />
                <g stroke="#5B4DFF" strokeWidth="2" strokeLinecap="round">
                  <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </g>
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" fill="#7C4DFF" opacity="0.8" />
              </svg>
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </HeaderUsageContext.Provider>
  );
};

function MobileBottomNav() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { s } = useLocale();
  return (
    <Box sx={{
      position: 'fixed', bottom: 0, left: 0, right: 0, height: '68px',
      backgroundColor: isDark ? '#1A1D2E' : '#FFFFFF',
      borderTop: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.08)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-around',
      zIndex: 1200, px: 1,
      boxShadow: isDark ? 'none' : '0 -2px 8px rgba(0,0,0,0.06)',
    }}>
      {[
        { label: s.nav_home, path: '/dashboards/all-games' },
        { label: s.nav_search_label, path: '/dashboards/search' },
        { label: s.header_btn_create, path: '/dashboards/create-game' },
        { label: s.nav_profile, path: '/management/profile/details' },
      ].map((item) => (
        <Box key={item.label} component="a" href={item.path} sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
          color: isDark ? '#8A8FA8' : '#666', textDecoration: 'none',
          padding: '8px 12px', borderRadius: '12px', minWidth: '48px',
          '&:hover': { color: '#5B4DFF' },
        }}>
          <Box component="span" sx={{ fontSize: '10px', fontWeight: 600 }}>{item.label}</Box>
        </Box>
      ))}
    </Box>
  );
}

export default StoryCraftLayout;
